/**
 * The stored-original rendering logic, shared by the Document Detail screen
 * (with its zoom control and mime/size facts bar) and the Reviewer screen
 * (plain, no chrome of its own) — both just need "given this preview state,
 * draw the right thing": an `<iframe>` for a PDF, an `<img>` for an image,
 * text as text, and an honest notice for everything else. Self-contained
 * (imports its own stylesheet) so either caller gets the right styles just by
 * importing this module.
 */
import type { DocumentView } from '../../api/types';
import { Button, Skeleton } from '../../components';

import './documents.css';
import { noPreviewReason } from './preview';
import type { useDocumentPreview } from './useDocumentPreview';

export function PreviewBody({
  state,
  doc,
  zoom,
  previewable,
  onDownload,
}: {
  state: ReturnType<typeof useDocumentPreview>;
  doc: DocumentView;
  zoom: number;
  previewable: boolean;
  onDownload?: () => void;
}): React.JSX.Element {
  if (!previewable) {
    return <PreviewNotice title="No preview" body="This document's original is no longer stored." />;
  }
  if (state.status === 'unsupported') {
    return (
      <PreviewNotice
        title="Preview not available"
        body={noPreviewReason(doc.mime)}
        onDownload={onDownload}
      />
    );
  }
  if (state.status === 'loading') {
    return (
      <div className="ds-docd__preview-loading" aria-busy="true" aria-label="Loading preview">
        <Skeleton width="86%" height="88%" radius={4} />
      </div>
    );
  }
  if (state.status === 'missing') {
    return (
      <PreviewNotice
        title="The original isn't available"
        body="Its stored file is gone — it may have been removed under your retention policy. The document's metadata and extracted text are unaffected."
      />
    );
  }
  if (state.status === 'error') {
    return (
      <PreviewNotice
        title="We couldn't load the preview"
        body="The original didn't load. This only affects the preview — the tabs beside it are unaffected."
        onRetry={state.reload}
      />
    );
  }
  if (state.status === 'text') {
    return <pre className="ds-docd__preview-text">{state.text}</pre>;
  }
  if (state.kind === 'pdf') {
    return (
      <iframe
        className="ds-docd__preview-frame"
        src={state.url}
        // An iframe is a document, not an image: it needs a title, not alt text.
        title={`Preview of ${doc.filename}`}
      />
    );
  }
  return (
    <img
      className="ds-docd__preview-img"
      src={state.url}
      alt={`Scanned page of ${doc.filename}`}
      style={{ transform: `scale(${String(zoom)})` }}
    />
  );
}

export function PreviewNotice({
  title,
  body,
  onDownload,
  onRetry,
}: {
  title: string;
  body: string;
  onDownload?: () => void;
  onRetry?: () => void;
}): React.JSX.Element {
  return (
    <div className="ds-docd__preview-notice">
      <span className="ds-docd__preview-notice-icon" aria-hidden="true">
        ⎗
      </span>
      <p className="ds-docd__preview-notice-title">{title}</p>
      <p className="ds-docd__preview-notice-body">{body}</p>
      {onDownload !== undefined ? (
        <Button variant="secondary" size="sm" onClick={onDownload}>
          Download original
        </Button>
      ) : null}
      {onRetry !== undefined ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}
